"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SearchService = void 0;
const TimeMetrics_1 = require("./TimeMetrics");
const stemmer_1 = __importDefault(require("stemmer"));
const EnvironmentService_1 = require("../EnvironmentService/EnvironmentService");
class SearchService {
    constructor() {
        this.platform = window.amp?.platform;
        this.track = this.platform?.track;
        this.srMetrics = (0, TimeMetrics_1.TimeMetrics)('SR Timing Metrics');
        this.numResultsPerPage = 10;
        this.options = { sp_queries: {}, urlHostPage: '' };
        this.logInfo('SearchService initialized');
    }
    logInfo(message) {
        if (typeof this.track?.info === 'function') {
            this.track.info(message);
        }
    }
    static getCollectionId() {
        return EnvironmentService_1.EnvironmentService.isEnvironment('prod')
            ? SearchService.PROD_COLLECTION_ID
            : SearchService.NON_PROD_COLLECTION_ID;
    }
    async fetchSearchData(query, category, spn) {
        this.srMetrics.createBrowserPerformanceRun();
        this.srMetrics.getReport();
        this.srMetrics.recordTimeStamp('start');
        const response = await this.runWatsonSearch(query, category, spn);
        return this.watsonSearchSuccess(query, category, spn, response);
    }
    async runWatsonSearch(searchQuery, category, spn) {
        const headers = { 'Content-Type': 'application/json' };
        const data = {
            natural_language_query: searchQuery,
            collectionIds: [SearchService.getCollectionId()],
            offset: parseInt(spn.toString()),
            spelling_suggestions: true,
            xReturn: [
                'document_id',
                'title',
                'extracted_metadata.title',
                'extracted_metadata.subject',
                'extracted_metadata.file_type',
                'extracted_metadata.filename',
                'url',
                'metadata.source.url',
                'enriched_html'
            ]
        };
        const baseFilter = 'metadata.source.url:!*=*';
        if (category) {
            data.filter = `${baseFilter},enriched_html.entities.text::"${decodeURIComponent(category)}",enriched_html.entities.model_name::Category`;
        }
        else {
            data.filter = baseFilter;
        }
        const response = await fetch('/api/ampf/search', {
            method: 'POST',
            headers,
            body: JSON.stringify(data)
        });
        return response.json();
    }
    watsonSearchSuccess(searchQuery, category, spn, response) {
        this.srMetrics.recordTimeStamp('rcv resp');
        const startOffset = parseInt(spn.toString());
        const resultList = response.results || [];
        const numTotalResults = response.matchingResults || 0;
        const userQuery = searchQuery.replace(/%20/g, '+').replace(/\+/g, ' ');
        const urlQuery = `?sp_q=${userQuery}&sp_q_1=${category.replace(/%20/g, '+')}`;
        const baseData = {
            userQuery,
            resultsTotal: numTotalResults,
            resultsLower: startOffset + 1,
            resultsUpper: startOffset + this.numResultsPerPage,
            resultsPage: startOffset + this.numResultsPerPage,
            resultsPagePrev: startOffset - this.numResultsPerPage <= 0
                ? 1
                : startOffset - this.numResultsPerPage,
            resultsPageNext: startOffset + this.numResultsPerPage >= numTotalResults
                ? 0
                : startOffset + this.numResultsPerPage
        };
        const paginationData = this.getWatsonPaginationData(baseData, urlQuery);
        const queryTermsRegExp = this.getQueryHighlightTerms(userQuery);
        this.srMetrics.recordTimeStamp('rendered');
        this.srMetrics.getReport();
        this.logInfo('BASE : ' + JSON.stringify({ baseData, paginationData }));
        return {
            suggestedQuery: response.suggestedQuery,
            searchResults: resultList,
            paginationData,
            queryTermsRegExp
        };
    }
    getWatsonPaginationData(sr, currentSearchUrl) {
        const pageLinksMax = 9;
        const pageLinksMovingMargin = Math.floor(pageLinksMax / 2);
        const pages = [];
        const previous = { pageStartItemIndex: 0 };
        const next = { pageStartItemIndex: 0 };
        const last = {};
        const totalPages = Math.ceil(sr.resultsTotal / this.numResultsPerPage);
        const currentStartIndex = sr.resultsLower;
        const currentPageIndex = Math.ceil(currentStartIndex / this.numResultsPerPage);
        const startPageIndex = Math.max(1, Math.min(currentPageIndex - pageLinksMovingMargin, totalPages - pageLinksMax + 1));
        const endPageIndex = Math.min(startPageIndex + pageLinksMax - 1, totalPages);
        const resultsLower = sr.resultsLower;
        const resultsUpper = sr.resultsUpper;
        const resultsTotal = sr.resultsTotal;
        this.logInfo(`Search: getPaginationData: ${startPageIndex}-${endPageIndex}`);
        for (let p = startPageIndex; p <= endPageIndex; p++) {
            const pageStartItemIndex = (p - 1) * this.numResultsPerPage + 1;
            pages.push({
                pageNumber: p,
                pageStartItemIndex: pageStartItemIndex - 1,
                currentPageClass: currentStartIndex === pageStartItemIndex ? 'current-page' : ''
            });
            if (p === totalPages) {
                last.pageStartItemIndex = pageStartItemIndex - 1;
            }
        }
        if (sr.resultsPagePrev > 0) {
            previous.pageStartItemIndex = sr.resultsLower - this.numResultsPerPage;
        }
        if (sr.resultsPageNext > 0) {
            next.pageStartItemIndex = sr.resultsLower + this.numResultsPerPage - 1;
        }
        return {
            urlHostPage: this.options.urlHostPage || '',
            currentSearchUrl,
            first: { pageStartItemIndex: 1 },
            previous,
            next,
            last,
            pages,
            totalPages,
            resultsPerPage: this.numResultsPerPage,
            currentStartIndex,
            currentPageIndex,
            startPageIndex,
            endPageIndex,
            resultsLower,
            resultsUpper: Math.min(resultsUpper, resultsTotal),
            resultsTotal
        };
    }
    getQueryHighlightTerms(query) {
        if (!query || typeof query !== 'string')
            return '';
        const ExcludePhrase = /-&quot;.+?&quot;/g;
        const QT = /&quot;/g;
        const PLUS = /\+/g;
        const SPC = /\s{2,}/g;
        const MINUS = /^-/;
        const terms = query
            .replace(ExcludePhrase, '')
            .replace(PLUS, '')
            .replace(QT, '')
            .replace(/\*/gi, '')
            .trim()
            .replace(SPC, ' ')
            .replace(/[-[\]{}()*+?.,\\^$|#]/g, '\\$&')
            .split(' ')
            .filter(term => !MINUS.test(term));
        const allTerms = [
            ...new Set([...terms, ...terms.map(term => (0, stemmer_1.default)(term))])
        ];
        const termsString = allTerms.join('|');
        return termsString ? `(${termsString})` : '';
    }
}
exports.SearchService = SearchService;
SearchService.PROD_COLLECTION_ID = 'f57443e4-017f-1eff-0000-018bf4c531b5';
SearchService.NON_PROD_COLLECTION_ID = '51a6c747-e0a4-f8bd-0000-019b27b5d294';
